import React from 'react';
import { Link } from 'react-router-dom';
import './about-v2.css';

/* ─────────────────────────────────────────────────────────────────────────
   ABOUT_CONFIG — single source of truth for all About page content.
   Swap image URLs or founder fields here; JSX never needs editing.
   ───────────────────────────────────────────────────────────────────────── */
const ABOUT_CONFIG = {

  /* ── Founder ─────────────────────────────────────────────────────────── */
  founder: {
    founderImageUrl: 'https://ik.imagekit.io/enrollforge/about/MyBio26.png?updatedAt=1787554855498',
    name: 'Larry Washington',
    title: 'Founder & Publisher, Opportunity Post',
    affiliations: [
      'Co-Founder, Student Signal & EnrollForge',
      'Strategic Investor, FIRM Institutional Benchmark',
    ],
    quote: '\u201cI build platforms that move markets.\u201d',
    bio: 'Larry Washington is the architect behind Opportunity Post — a premium intelligence platform engineered at the intersection of media, career access, and institutional capital. A serial builder in the education-technology and publishing space, he has spent more than a decade creating infrastructure that translates complex market movement into actionable clarity for professionals, students, and strategic partners.',
    bandQuote: 'His work sits at the intersection of editorial authority, career intelligence, and institutional investment — three levers that, together, move markets.',
    linkedInUrl: '/go/founder-linkedin',
    talkUrl: '/contact',
    credentials: [
      { value: '3',    label: 'Platforms Founded' },
      { value: '10+',  label: 'Years in Ed-Tech' },
      { value: '1K+',  label: 'Opportunities Surfaced' },
      { value: '6',    label: 'Platform Engines' },
    ],
  },

  /* ── Images ──────────────────────────────────────────────────────────── */
  images: {
    hero: [
      'https://ik.imagekit.io/enrollforge/about/hero-editorial-reader.webp?updatedAt=1787457080507',
      'https://ik.imagekit.io/enrollforge/about/Hiringboss.webp?updatedAt=1787458019096',
      'https://ik.imagekit.io/enrollforge/about/hero-advisor-student.webp?updatedAt=1787457080477',
      'https://ik.imagekit.io/enrollforge/about/hero-sector-intelligence.webp?updatedAt=1787457080546',
    ],
    purposeEditorial: 'https://ik.imagekit.io/enrollforge/about/editorial-workspace.webp?updatedAt=1787456879302',
    careerPanel:      'https://ik.imagekit.io/enrollforge/about/Techerwork.webp?updatedAt=1787458019159',
    briefcasePanel:   'https://ik.imagekit.io/enrollforge/about/first-briefcase-mentor.webp?updatedAt=1787456879403',
    magCover:         'https://ik.imagekit.io/enrollforge/about/Partner_reading_Mag.webp?updatedAt=1787458019368',
    magReader:        'https://ik.imagekit.io/enrollforge/about/magazine-reader.webp?updatedAt=1787456879355',
    recognitionA:     'https://ik.imagekit.io/enrollforge/about/company-in-focus.webp?updatedAt=1787456879383',
    recognitionB:     'https://ik.imagekit.io/enrollforge/about/company-in-focus_44E7Kfe64m.webp?updatedAt=1787458018589',
    finalImage:       'https://ik.imagekit.io/enrollforge/about/edupartner.webp?updatedAt=1787458019112',
  },

  /* ── Four Ways to Engage ─────────────────────────────────────────────── */
  engage: [
    {
      eyebrow: 'Career Hub',
      title: 'Curated opportunity for emerging talent.',
      copy: 'Thousands of verified roles, institutional listings, and career-intelligence resources in one precision-built platform.',
      cta: 'Explore Career Hub', href: '/career-hub', testid: 'engage-career-hub-btn',
      img: 'https://ik.imagekit.io/enrollforge/about/first-briefcase-mentor_JdKwt5PbL.webp?updatedAt=1787458018326',
    },
    {
      eyebrow: 'Employer Visibility',
      title: 'Reach the talent that moves markets.',
      copy: 'Post roles, sponsor editorial, and activate brand visibility across the most credible opportunity platform for professional audiences.',
      cta: 'Explore Employer Visibility', href: '/career-hub', testid: 'engage-employer-btn',
      img: 'https://ik.imagekit.io/enrollforge/about/Hiringboss.webp?updatedAt=1787458019096',
    },
    {
      eyebrow: 'Advertise With Us',
      title: 'Premium placement for premium brands.',
      copy: 'Our editorial platform delivers institutional audiences to your message — in a context that commands attention and earns trust.',
      cta: 'Request Media Kit', href: '/contact', testid: 'engage-advertise-btn',
      img: 'https://ik.imagekit.io/enrollforge/about/advertiser-editorial.webp?updatedAt=1787457080474',
    },
    {
      eyebrow: 'Institutional Partnerships',
      title: 'Build something larger together.',
      copy: 'Universities, workforce agencies, and professional associations partner with Opportunity Post to amplify signal, access, and brand authority.',
      cta: 'Explore Partnerships', href: '/contact', testid: 'engage-partnerships-btn',
      img: 'https://ik.imagekit.io/enrollforge/about/college-workforce.webp?updatedAt=1787457080460',
    },
  ],

  /* ── Platform Products ───────────────────────────────────────────────── */
  products: [
    { num: '01', title: 'Opportunity Post',  copy: 'The editorial foundation — precision news and analysis across opportunity markets, career intelligence, and institutional movement.', href: '/',            cta: 'Visit' },
    { num: '02', title: 'First Briefcase',   copy: 'Executive-grade briefing intelligence designed to equip early-stage professionals with context that powers confident decisions.',   href: '/first-round',  cta: 'Access' },
    { num: '03', title: 'Career Hub',        copy: 'A curated job platform connecting emerging professionals with roles across education, enterprise, and institutional sectors.',        href: '/career-hub',   cta: 'Explore' },
    { num: '04', title: 'The Magazine',      copy: 'Premium quarterly long-form editorial — deep-dive reporting and visual storytelling for professionals who invest in what they read.', href: '/magazine',     cta: 'Read' },
    { num: '05', title: 'Student Signal',    copy: 'Enrollment intelligence and market signal for higher education institutions and the professionals navigating the admission ecosystem.', href: null, cta: 'External Platform' },
    { num: '06', title: 'EnrollForge',       copy: 'A higher education enrollment engine connecting institutions with prospective students through verified intelligence and precision targeting.', href: null, cta: 'External Platform' },
  ],

  /* ── How Opportunity Moves ───────────────────────────────────────────── */
  stages: [
    { num: '01', title: 'Signal Detected',   copy: 'Opportunity Post monitors institutional movement, editorial signals, and market shifts — identifying the stories that matter before they surface.' },
    { num: '02', title: 'Analysis Published', copy: 'Editorial teams frame, verify, and publish with institutional clarity — building context that decision-makers can act on immediately.' },
    { num: '03', title: 'Distributed',       copy: 'Multi-surface distribution activates across newsletters, Career Hub, First Briefcase, and the Magazine — reaching the right professional at the right moment.' },
    { num: '04', title: 'Decision Made',     copy: 'Professionals with the right intelligence at the right time make better moves — for careers, institutions, and the markets they serve.' },
  ],

  /* ── Return Rhythm ───────────────────────────────────────────────────── */
  rhythm: [
    { cadence: 'Daily',     title: 'Morning Signal',    copy: 'Editorial coverage and market movement delivered before the professional day begins.' },
    { cadence: 'Weekly',    title: 'Sector Briefings',  copy: 'Deep-category intelligence across education, enterprise, and institutional sectors — summarized for efficient review.' },
    { cadence: 'Monthly',   title: 'Career Signals',    copy: 'Curated opportunity batches from Career Hub, surfacing roles and access points aligned with reader intent.' },
    { cadence: 'Quarterly', title: 'The Magazine',      copy: 'Premium long-form editorial in a beautifully composed issue — the best of Opportunity Post, in full.' },
  ],
};

/* ──────────────────────────────────────────────────────────────────────────
   Page component — no header / footer / nav: OP shell provides those.
   ────────────────────────────────────────────────────────────────────────── */
export default function AboutPage() {
  const { founder, images, engage, products, stages, rhythm } = ABOUT_CONFIG;

  return (
    <div className="abv2" data-testid="about-page">
      <main id="main-content">

        {/* ═══════════════════════════════════════
            1. HERO
        ═══════════════════════════════════════ */}
        <section className="au-hero">
          <div className="au-inner">
            <div className="au-hero-grid">
              <div>
                <span className="au-eyebrow">About Opportunity Post</span>
                <h1>The authority layer for modern opportunity.</h1>
                <p className="au-lead">
                  Opportunity Post is built to translate complex market movement into
                  editorial-grade intelligence — presenting opportunity with rigor, visual
                  precision, and the institutional clarity premium partners expect.
                </p>
                <div className="au-btn-row">
                  <Link className="au-btn au-btn-primary" to="/contact" data-testid="about-hero-talk-btn">
                    Talk to Our Team
                  </Link>
                  <a className="au-btn au-btn-secondary" href="#platform" data-testid="about-hero-explore-btn">
                    Explore the Platform
                  </a>
                </div>
              </div>
              <div>
                <div className="au-hero-collage">
                  {images.hero.map((src, i) => (
                    <figure key={i}><img src={src} alt="" /></figure>
                  ))}
                </div>
                <div className="au-hero-proof" aria-label="Key platform statistics">
                  <div><strong>30+</strong><span>Editorial signals tracked daily</span></div>
                  <div><strong>50+</strong><span>Strategic content surfaces</span></div>
                  <div><strong>6</strong><span>Core platform engines</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            2. FOUR WAYS TO ENGAGE
        ═══════════════════════════════════════ */}
        <section className="au-section" style={{ background: 'var(--au-paper)' }}>
          <div className="au-inner">
            <div className="au-section-title">
              <span className="au-eyebrow">Engage With Us</span>
              <h2>Four ways to connect with Opportunity Post.</h2>
            </div>
            <div className="au-engage-grid">
              {engage.map((card) => (
                <article
                  className="au-card au-engage-card"
                  key={card.eyebrow}
                  data-testid={`engage-card-${card.testid.replace('-btn', '')}`}
                >
                  <figure>
                    <img src={card.img} alt={card.title} />
                  </figure>
                  <div className="au-engage-body">
                    <span className="au-eyebrow">{card.eyebrow}</span>
                    <h3>{card.title}</h3>
                    <p>{card.copy}</p>
                    <Link className="au-btn au-btn-primary" to={card.href} data-testid={card.testid}>
                      {card.cta}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            3. OPPORTUNITY POST ECOSYSTEM
        ═══════════════════════════════════════ */}
        <section className="au-ecosystem au-dark">
          <div className="au-inner">
            <span className="au-eyebrow">The Ecosystem</span>
            <h2>One platform. Multiple intelligence surfaces.</h2>
            <p className="au-lead" style={{ color: '#c2cdd7' }}>
              Every layer of Opportunity Post is engineered to move information with clarity —
              from editorial signal to career placement to institutional intelligence.
            </p>
            <div className="au-pillars">
              {[
                { n: '01', title: 'Editorial',       copy: 'Independent journalism built on market clarity and institutional discipline.' },
                { n: '02', title: 'Career Hub',      copy: 'Curated opportunity intelligence connecting emerging professionals with meaningful roles.' },
                { n: '03', title: 'First Briefcase', copy: 'Executive-grade briefing products delivering signal to decision-makers in motion.' },
                { n: '04', title: 'The Magazine',    copy: 'Premium long-form coverage crafted for professionals who read to lead.' },
              ].map((p) => (
                <div className="au-pillar" key={p.n}>
                  <div className="au-pillar-icon">{p.n}</div>
                  <strong>{p.title}</strong>
                  <p style={{ color: '#a8b9c8', fontSize: '.95rem', marginTop: '8px' }}>{p.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            4. OUR PURPOSE
        ═══════════════════════════════════════ */}
        <section className="au-section">
          <div className="au-inner">
            <div className="au-split">
              <div>
                <span className="au-eyebrow">Our Purpose</span>
                <h2>We translate opportunity into clarity.</h2>
                <p>
                  Premium decision-makers need more than noise — they need context, precision, and
                  a publication that respects their intelligence. Opportunity Post was built to be
                  that standard: an editorial layer that knows what's happening before most rooms
                  do, and says it in a way that holds up in the boardroom.
                </p>
                <div className="au-editorial-image">
                  <img src={images.purposeEditorial} alt="Opportunity Post editorial team" />
                </div>
              </div>
              <div className="au-mini-stack">
                {[
                  { label: 'For Decision Makers',       title: 'Intelligence without friction.',      copy: 'Executive-grade coverage that surfaces what matters — delivered with the precision and clarity that serious professionals require.' },
                  { label: 'For Emerging Professionals', title: 'Access built into every surface.',   copy: 'From Career Hub listings to First Briefcase essentials, every product helps emerging talent navigate the opportunity landscape.' },
                  { label: 'For Strategic Partners',    title: 'Brand authority, fully realized.',   copy: 'Institutional partners access a premium editorial platform that elevates their brand signal and reaches verified professional audiences.' },
                ].map((c) => (
                  <article className="au-mini-card au-card" key={c.label}>
                    <span className="au-eyebrow">{c.label}</span>
                    <h3>{c.title}</h3>
                    <p>{c.copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            5. MEET THE FOUNDER
        ═══════════════════════════════════════ */}
        <section className="au-founder-wrap">
          <div className="au-inner">
            <div className="au-founder-card au-card">
              <div className="au-founder-grid">

                {/* Left column: portrait + CTAs */}
                <div className="au-founder-left">
                  <div className="au-founder-photo">
                    <img
                      src="https://customer-assets-gfyr7b9c.emergentagent.net/job_0268822e-576e-45ce-8ebc-60c75f327055/artifacts/gw5ihha8_Bio.jpg"
                      alt="Larry Washington"
                      style={{ borderRadius: '16px 16px 0 0' }}
                    />
                  </div>
                  <div className="au-founder-ctas">
                    <a
                      href={founder.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="au-btn au-btn-gold"
                      data-testid="founder-linkedin-btn"
                    >
                      Connect on LinkedIn
                    </a>
                    <a
                      href={founder.talkUrl}
                      className="au-btn au-btn-founder-ghost"
                      data-testid="founder-talk-btn"
                    >
                      Talk to Larry
                    </a>
                  </div>
                </div>

                {/* Right column: copy */}
                <div className="au-founder-copy">
                  <span className="au-eyebrow">Meet the Founder</span>
                  <h2>{founder.name}</h2>
                  <div className="au-founder-role">{founder.title}</div>
                  <div className="au-founder-affil">
                    {founder.affiliations.map((a, i) => (
                      <React.Fragment key={i}>
                        <span>{a}</span>
                        {i < founder.affiliations.length - 1 && (
                          <span className="au-affil-sep"> · </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <blockquote className="au-founder-quote">{founder.quote}</blockquote>
                  <p>{founder.bio}</p>
                  <div className="au-founder-band">{founder.bandQuote}</div>
                </div>
              </div>

              {/* Credentials bar — full-width at card bottom */}
              <div className="au-founder-credentials" aria-label="Founder credentials">
                {founder.credentials.map((c) => (
                  <div className="au-cred" key={c.label}>
                    <strong>{c.value}</strong>
                    <span>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            6. PLATFORM
        ═══════════════════════════════════════ */}
        <section id="platform" className="au-section au-product-bg">
          <div className="au-inner">
            <div className="au-section-title">
              <span className="au-eyebrow">The Platform</span>
              <h2>Six engines. One unified intelligence system.</h2>
              <p className="au-lead">
                Each product serves a distinct function — together, they form a complete
                ecosystem for market-moving intelligence.
              </p>
            </div>
            <div className="au-product-grid">
              {products.map((p) => (
                <article className="au-product au-card" key={p.num}>
                  <div className="au-num">{p.num}</div>
                  <h3>{p.title}</h3>
                  <p>{p.copy}</p>
                  {p.href
                    ? (
                      <Link to={p.href} className="au-product-link" data-testid={`platform-${p.num}-link`}>
                        {p.cta} &rarr;
                      </Link>
                    )
                    : <span className="au-product-external">{p.cta}</span>
                  }
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            7. HOW OPPORTUNITY MOVES
        ═══════════════════════════════════════ */}
        <section className="au-section au-dark">
          <div className="au-inner">
            <span className="au-eyebrow">How Opportunity Moves</span>
            <h2>From signal to decision, in four stages.</h2>
            <div className="au-pathway">
              {stages.map((s) => (
                <article className="au-stage au-stage-dark" key={s.num}>
                  <div className="au-stage-num">{s.num}</div>
                  <h3>{s.title}</h3>
                  <p>{s.copy}</p>
                </article>
              ))}
            </div>
            <div className="au-progression">
              The full cycle plays out across every Opportunity Post product, every day.
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            8. CAREER + FIRST BRIEFCASE
        ═══════════════════════════════════════ */}
        <section className="au-section">
          <div className="au-inner">
            <div className="au-section-title">
              <span className="au-eyebrow">Career + First Briefcase</span>
              <h2>Built for professionals in motion.</h2>
            </div>
            <div className="au-duo">
              <article className="au-feature-panel au-card">
                <figure>
                  <img src={images.careerPanel} alt="Career Hub — curated job opportunities" />
                </figure>
                <div className="au-feature-body">
                  <span className="au-eyebrow">Career Hub</span>
                  <h3>Where opportunity becomes actionable.</h3>
                  <p>Career Hub connects emerging professionals with curated roles, institutional partners, and the editorial context to make better moves — faster.</p>
                  <Link className="au-btn au-btn-primary" to="/career-hub" data-testid="about-career-hub-btn">
                    Find Opportunities
                  </Link>
                </div>
              </article>
              <article className="au-feature-panel au-card">
                <figure>
                  <img src={images.briefcasePanel} alt="First Briefcase — executive intelligence briefing" />
                </figure>
                <div className="au-feature-body">
                  <span className="au-eyebrow">First Briefcase</span>
                  <h3>The essential briefing for professionals entering the room.</h3>
                  <p>First Briefcase delivers executive-grade signal, curated for ambitious professionals who need the right context — on time, every time.</p>
                  <Link className="au-btn au-btn-primary" to="/first-round" data-testid="about-first-briefcase-btn">
                    Access the Briefcase
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            9. MAGAZINE
        ═══════════════════════════════════════ */}
        <section className="au-section au-product-bg">
          <div className="au-inner">
            <div className="au-mag-grid">
              <div className="au-mag-visual">
                <div className="au-mag-cover">
                  <img src={images.magCover} alt="Opportunity Post Magazine cover" />
                </div>
                <div className="au-mag-screen">
                  <div>
                    <span className="au-ui-chip">Feature</span>
                    <span className="au-ui-chip">Deep Dive</span>
                  </div>
                  <div className="au-screen-block" />
                  <div className="au-screen-block" style={{ minHeight: '60px' }} />
                </div>
              </div>
              <div>
                <span className="au-eyebrow">The Magazine</span>
                <h2>Long-form intelligence, beautifully composed.</h2>
                <p>The Opportunity Post Magazine is premium long-form coverage for professionals who read to lead. Each issue combines rigorous editorial with visual precision — designed for the desk, the conference table, and the commute that matters.</p>
                <ul className="au-mag-list">
                  <li>Quarterly issues with feature-length editorial coverage</li>
                  <li>Visual design calibrated for executive credibility</li>
                  <li>Deep-dive reporting on market movement and institutional opportunity</li>
                </ul>
                <Link className="au-btn au-btn-primary" to="/magazine" data-testid="about-magazine-btn">
                  Read the Magazine
                </Link>
                <div className="au-mag-reader-photo">
                  <img src={images.magReader} alt="Professional reading Opportunity Post Magazine" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            10. RECOGNITION
        ═══════════════════════════════════════ */}
        <section className="au-section">
          <div className="au-inner">
            <div className="au-section-title">
              <span className="au-eyebrow">Recognition</span>
              <h2>Trusted by the community we serve.</h2>
            </div>
            <div className="au-recognition-grid">
              <article className="au-recognition-card au-card">
                <figure>
                  <img src={images.recognitionA} alt="Industry recognition" />
                </figure>
                <div className="au-recognition-body">
                  <h3>Building platforms that earn trust.</h3>
                  <p>Opportunity Post has established itself as a credible, independent voice in education intelligence, career access, and institutional publishing — recognized for editorial discipline and platform integrity.</p>
                </div>
              </article>
              <article className="au-recognition-card au-card">
                <figure>
                  <img src={images.recognitionB} alt="Community trust" />
                </figure>
                <div className="au-recognition-body">
                  <h3>A community-verified standard.</h3>
                  <p>From institutions to individual professionals, the audiences that matter most have chosen Opportunity Post as their signal source for what moves the opportunity market.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            11. RETURN RHYTHM
        ═══════════════════════════════════════ */}
        <section className="au-section">
          <div className="au-inner">
            <span className="au-eyebrow">Return Rhythm</span>
            <h2>We earn your return, daily.</h2>
            <p className="au-lead">
              Every product, every publishing cycle, and every surface is built to deliver
              consistent value on a schedule professionals can rely on.
            </p>
            <div className="au-rhythm-grid">
              {rhythm.map((r) => (
                <article className="au-rhythm au-card" key={r.cadence}>
                  <span className="au-cadence">{r.cadence}</span>
                  <h3>{r.title}</h3>
                  <p>{r.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════
            12. WORK WITH OPPORTUNITY POST
        ═══════════════════════════════════════ */}
        <section className="au-section au-dark">
          <div className="au-inner">
            <div className="au-final-grid">
              <div>
                <span className="au-eyebrow">Work With Opportunity Post</span>
                <h2>Ready to move with the platform?</h2>
                <p className="au-final-lead">
                  Whether you're a strategic partner, institutional sponsor, or an organization
                  looking to reach premium professional audiences — Opportunity Post has a model
                  built for you.
                </p>
                <div className="au-btn-row">
                  <Link className="au-btn au-btn-gold" to="/contact" data-testid="about-work-contact-btn">
                    Get in Touch
                  </Link>
                  <Link
                    className="au-btn au-btn-work-ghost"
                    to="/subscribe"
                    data-testid="about-work-subscribe-btn"
                  >
                    Subscribe to a Product
                  </Link>
                </div>
                <nav className="au-link-routes" aria-label="Platform quick links">
                  <Link to="/magazine"   data-testid="work-link-magazine">The Magazine</Link>
                  <Link to="/career-hub" data-testid="work-link-careerhub">Career Hub</Link>
                  <Link to="/first-round" data-testid="work-link-briefcase">First Briefcase</Link>
                  <Link to="/subscribe"  data-testid="work-link-subscribe">Newsletters</Link>
                </nav>
              </div>
              <figure className="au-final-image">
                <img src={images.finalImage} alt="Opportunity Post editorial platform" />
              </figure>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}
